import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import DiagnosticoSelectorV2 from "./DiagnosticoSelectorV2";
import {
  createEmptyDiagnosisV2,
  validateDiagnosesV2,
} from "../saludClinicaV2Utils";

const DiagnosticosV2 = ({ value = [], onChange, error = "", disabled = false }) => {
  const diagnoses = value.length ? value : [createEmptyDiagnosisV2()];
  const localValidation = useMemo(() => validateDiagnosesV2(diagnoses), [diagnoses]);

  const updateDiagnosis = (index, diagnosis) => {
    const next = [...diagnoses];
    next[index] = diagnosis;
    onChange(next);
  };

  const removeDiagnosis = (index) => {
    if (diagnoses.length === 1) {
      onChange([createEmptyDiagnosisV2()]);
      return;
    }
    onChange(diagnoses.filter((item, currentIndex) => currentIndex !== index));
  };

  return (
    <Box width="100%">
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} mb={1.5}>
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Diagnósticos clínicos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            El primer diagnóstico es el principal. Puede registrar hasta cinco.
          </Typography>
        </Box>
        <Chip label={`${diagnoses.length}/5`} size="small" />
      </Stack>

      <Stack spacing={1.5} divider={<Divider flexItem />}>
        {diagnoses.map((diagnosis, index) => {
          const excludedConceptIds = diagnoses
            .filter((item, itemIndex) => itemIndex !== index)
            .map((item) => item.idConcepto);

          return (
            <Paper key={`${index}-${diagnosis.idConcepto || "pending"}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Chip
                    icon={index === 0 ? <StarOutlinedIcon /> : undefined}
                    label={index === 0 ? "Principal" : `Diagnóstico ${index + 1}`}
                    color={index === 0 ? "primary" : "default"}
                    size="small"
                  />
                  {diagnosis.pendienteRevision && (
                    <Chip label="Pendiente de revisión" color="warning" variant="outlined" size="small" />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center">
                  <Tooltip title="Eliminar diagnóstico">
                    <span>
                      <IconButton
                        type="button"
                        color="error"
                        onClick={() => removeDiagnosis(index)}
                        disabled={disabled}
                        aria-label={`Eliminar diagnóstico ${index + 1}`}
                      >
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              <DiagnosticoSelectorV2
                value={diagnosis}
                onChange={(next) => updateDiagnosis(index, next)}
                excludedConceptIds={excludedConceptIds}
                error={error && index === 0 ? error : ""}
                disabled={disabled}
              />
            </Paper>
          );
        })}
      </Stack>

      <Button
        type="button"
        variant="outlined"
        startIcon={<AddOutlinedIcon />}
        onClick={() => onChange([...diagnoses, createEmptyDiagnosisV2()])}
        disabled={disabled || diagnoses.length >= 5 || Boolean(localValidation && diagnoses.length > 1)}
        sx={{ mt: 1.5 }}
      >
        Añadir diagnóstico
      </Button>
    </Box>
  );
};

export default DiagnosticosV2;
